/**
 * animatedScrollIntoView — SmoothDamp Scroll Engine 2D
 */
import { MAX_WIDTH_MOBILE, MAX_WIDTH_TABLET } from "../constants/dimension";
import { getMediaSizeScale } from "../models/screen";
import { isTV } from "./platform";

const SMOOTH_TIME_X = 0.12;
const SMOOTH_TIME_Y = 0.12;
const SMOOTH_TIME_TV_TAP = 0.08;
const SETTLE_THRESHOLD = 1.0;
const MAX_FRAME_DT = 0.033;
// padding
const H_PADDING_DESKTOP = 90;
const H_PADDING_MOBILE = 16;
export const getHPadding = () => {
  /**
   * Compute locally instead of importing from constants/dimension: those flags
   * are frozen at module load, so they don't update on rotation (portrait ->
   * landscape). Reading `window` per call keeps the padding correct after rotate
   * 
   */
  const isDesktop = window.innerWidth > MAX_WIDTH_TABLET || isTV;
  const isMobile = window.innerWidth < MAX_WIDTH_MOBILE;
  const isTablet = !isMobile && !isDesktop;
  const isTabletLandscape =
    isTablet && window.innerWidth > window.innerHeight;

  return isDesktop || isTabletLandscape
    ? H_PADDING_DESKTOP * getMediaSizeScale()
    : H_PADDING_MOBILE;
};

const V_PADDING_TOP = 0;
const V_PADDING_BOTTOM = 0;

// Max speed limits to prevent physics explosion
const MAX_SPEED_X = 6000;
const MAX_SPEED_Y = 6000;

// Cached DOM refs
const _docEl = document.documentElement;
const _body = document.body;
const _scrollingEl = document.scrollingElement as HTMLElement;
const _root = (_scrollingEl ?? _docEl) as HTMLElement;

export interface AnimatedScrollOptions {
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
  axis?: "x" | "y" | "both";
  offsetBottom?: number;
  isRepeating?: boolean;
}

interface ScrollAnimState {
  isRoot: boolean; // cached
  currentX: number;
  targetX: number;
  velX: number;
  currentY: number;
  targetY: number;
  velY: number;
  lastWrittenX: number;
  lastWrittenY: number; // Cache DOM write
  pLeft: number;
  pRight: number;
  pTop: number;
  pBottom: number;
  isRepeating: boolean;
}

const scrollStates = new Map<HTMLElement, ScrollAnimState>();
let globalAnimId: number | null = null;
let lastTime = 0;

function globalStep(now: number): void {
  let dt = (now - lastTime) / 1000;
  if (dt <= 0) dt = 1 / 60;
  if (dt > MAX_FRAME_DT) dt = MAX_FRAME_DT;
  lastTime = now;

  let stillAnimating = false;

  for (const [el, state] of scrollStates.entries()) {
    const { isRoot, isRepeating } = state;
    // TV tap override: snappier single-press response
    const smoothTimeX =
      isTV && !isRepeating ? SMOOTH_TIME_TV_TAP : SMOOTH_TIME_X;
    const smoothTimeY =
      isTV && !isRepeating ? SMOOTH_TIME_TV_TAP : SMOOTH_TIME_Y;
    const omegaX = 2.0 / smoothTimeX;
    const omegaY = 2.0 / smoothTimeY;
    const maxDeltaX = MAX_SPEED_X * smoothTimeX;
    const maxDeltaY = MAX_SPEED_Y * smoothTimeY;

    const xX = omegaX * dt;
    const expX = 1.0 / (1.0 + xX + 0.48 * xX * xX + 0.235 * xX * xX * xX);
    const xY = omegaY * dt;
    const expY = 1.0 / (1.0 + xY + 0.48 * xY * xY + 0.235 * xY * xY * xY);

    let dirty = false;

    // --- X Axis ---
    const absErrX = Math.abs(state.currentX - state.targetX);
    if (absErrX > SETTLE_THRESHOLD || Math.abs(state.velX) > SETTLE_THRESHOLD) {
      let deltaX = state.currentX - state.targetX;

      // Clamp delta and use adjusted target to prevent teleportation
      if (deltaX > maxDeltaX) deltaX = maxDeltaX;
      else if (deltaX < -maxDeltaX) deltaX = -maxDeltaX;
      const adjustedTargetX = state.currentX - deltaX;

      const tempX = (state.velX + omegaX * deltaX) * dt;
      state.velX = (state.velX - omegaX * tempX) * expX;
      state.currentX = adjustedTargetX + (deltaX + tempX) * expX;
      stillAnimating = true;
      dirty = true;
    } else if (absErrX > 0) {
      state.currentX = state.targetX;
      state.velX = 0;
      dirty = true;
    }

    // --- Y Axis ---
    const absErrY = Math.abs(state.currentY - state.targetY);
    if (absErrY > SETTLE_THRESHOLD || Math.abs(state.velY) > SETTLE_THRESHOLD) {
      let deltaY = state.currentY - state.targetY;

      // Clamp delta and use adjusted target to prevent teleportation
      if (deltaY > maxDeltaY) deltaY = maxDeltaY;
      else if (deltaY < -maxDeltaY) deltaY = -maxDeltaY;
      const adjustedTargetY = state.currentY - deltaY;

      const tempY = (state.velY + omegaY * deltaY) * dt;
      state.velY = (state.velY - omegaY * tempY) * expY;
      state.currentY = adjustedTargetY + (deltaY + tempY) * expY;
      stillAnimating = true;
      dirty = true;
    } else if (absErrY > 0) {
      state.currentY = state.targetY;
      state.velY = 0;
      dirty = true;
    }

    /**
     * Single write per element per frame
     * X and Y combined to avoid thrashing (with sub-pixel rounding)
     */
    if (dirty) {
      const rx = Math.round(state.currentX);
      const ry = Math.round(state.currentY);

      if (rx !== state.lastWrittenX || ry !== state.lastWrittenY) {
        if (isRoot) window.scrollTo(rx, ry);
        else {
          el.scrollLeft = rx;
          el.scrollTop = ry;
        }
        state.lastWrittenX = rx;
        state.lastWrittenY = ry;
      }
    }

    // Reuse pre-calculated absErrX/Y
    if (
      absErrX <= SETTLE_THRESHOLD &&
      absErrY <= SETTLE_THRESHOLD &&
      Math.abs(state.velX) < 1 &&
      Math.abs(state.velY) < 1
    ) {
      scrollStates.delete(el);
    }
  }

  if (stillAnimating && scrollStates.size > 0) {
    globalAnimId = requestAnimationFrame(globalStep);
  } else {
    globalAnimId = null;
  }
}

function findScrollableParent(el: HTMLElement, axis: "x" | "y"): HTMLElement {
  let parent = el.parentElement;
  // use cached refs
  while (parent && parent !== _docEl && parent !== _body) {
    const style = getComputedStyle(parent);
    if (
      axis === "x" &&
      (style.overflowX === "auto" || style.overflowX === "scroll") &&
      parent.scrollWidth > parent.clientWidth
    )
      return parent;
    if (
      axis === "y" &&
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      parent.scrollHeight > parent.clientHeight
    )
      return parent;
    parent = parent.parentElement;
  }
  return _root;
}

export function cancelScrollAnimation(): void {
  if (globalAnimId !== null) {
    cancelAnimationFrame(globalAnimId);
    globalAnimId = null;
  }
  scrollStates.clear();
}

export function animatedScrollIntoView(
  element: HTMLElement,
  {
    block = "nearest",
    inline = "start",
    axis = "both",
    offsetBottom = 0,
    isRepeating = false,
  }: AnimatedScrollOptions = {},
): void {
  if (axis === "x" || axis === "both")
    setupScroll(
      findScrollableParent(element, "x"),
      element,
      "x",
      inline,
      0,
      isRepeating,
    );
  if (axis === "y" || axis === "both")
    setupScroll(
      findScrollableParent(element, "y"),
      element,
      "y",
      block,
      offsetBottom,
      isRepeating,
    );
}

function setupScroll(
  parent: HTMLElement,
  targetEl: HTMLElement,
  axis: "x" | "y",
  alignment: ScrollLogicalPosition,
  extraOffset: number,
  isRepeating: boolean,
) {
  const isRoot =
    parent === _docEl || parent === _body || parent === _scrollingEl;

  let state = scrollStates.get(parent);
  if (!state) {
    const style = isRoot
      ? {
          scrollPaddingTop: String(V_PADDING_TOP),
          scrollPaddingBottom: String(V_PADDING_BOTTOM),
        }
      : getComputedStyle(parent);

    state = {
      isRoot,
      currentX: isRoot ? window.scrollX : parent.scrollLeft,
      targetX: isRoot ? window.scrollX : parent.scrollLeft,
      velX: 0,
      currentY: isRoot ? window.scrollY : parent.scrollTop,
      targetY: isRoot ? window.scrollY : parent.scrollTop,
      velY: 0,
      lastWrittenX: Math.round(isRoot ? window.scrollX : parent.scrollLeft),
      lastWrittenY: Math.round(isRoot ? window.scrollY : parent.scrollTop),
      pLeft: getHPadding(),
      pRight: getHPadding(),
      pTop:
        parseFloat(
          (style as any).scrollPaddingTop || (style as any).paddingTop,
        ) || 0,
      pBottom:
        parseFloat(
          (style as any).scrollPaddingBottom || (style as any).paddingBottom,
        ) || 0,
      isRepeating,
    };
    scrollStates.set(parent, state);
  } else {
    const actualX = isRoot ? window.scrollX : parent.scrollLeft;
    const actualY = isRoot ? window.scrollY : parent.scrollTop;
    if (Math.abs(state.currentX - actualX) > 1) {
      state.currentX = actualX;
      state.velX = 0;
      state.lastWrittenX = Math.round(actualX);
    }
    if (Math.abs(state.currentY - actualY) > 1) {
      state.currentY = actualY;
      state.velY = 0;
      state.lastWrittenY = Math.round(actualY);
    }
    // Update mode for ongoing animation (held -> released switches seamlessly)
    state.isRepeating = isRepeating;
  }

  // Batch reads: getBoundingClientRect called once per element, no interleaved writes
  const elRect = targetEl.getBoundingClientRect();
  const parentRect = isRoot ? null : parent.getBoundingClientRect();

  const parentTop = isRoot ? 0 : parentRect!.top;
  const parentLeft = isRoot ? 0 : parentRect!.left;
  const parentWidth = isRoot ? window.innerWidth : parent.clientWidth;
  const parentHeight = isRoot ? window.innerHeight : parent.clientHeight;
  const scrollLeftDOM = isRoot ? window.scrollX : parent.scrollLeft;
  const scrollTopDOM = isRoot ? window.scrollY : parent.scrollTop;

  if (axis === "x") {
    const offsetLeft = elRect.left - parentLeft;
    const offsetRight = elRect.right - (parentLeft + parentWidth);
    const absoluteLeft = scrollLeftDOM + offsetLeft;
    let targetLeft = scrollLeftDOM;

    if (alignment === "start") targetLeft = absoluteLeft - state.pLeft;
    else if (alignment === "center")
      targetLeft =
        absoluteLeft -
        state.pLeft -
        (parentWidth - state.pLeft - state.pRight - elRect.width) / 2;
    else if (alignment === "end")
      targetLeft = scrollLeftDOM + offsetRight + state.pRight;
    else {
      // nearest
      if (offsetLeft < state.pLeft) targetLeft = absoluteLeft - state.pLeft;
      else if (offsetRight > -state.pRight)
        targetLeft = scrollLeftDOM + offsetRight + state.pRight;
      else targetLeft = state.targetX;
    }

    const maxScrollX = isRoot
      ? _docEl.scrollWidth - window.innerWidth
      : parent.scrollWidth - parent.clientWidth;
    state.targetX = Math.max(0, Math.min(targetLeft, maxScrollX));
  }

  if (axis === "y") {
    const offsetTopActual = elRect.top - parentTop;
    const offsetBottom = elRect.bottom - (parentTop + parentHeight);
    const absoluteTop = scrollTopDOM + offsetTopActual;
    const bottomPadding = state.pBottom + extraOffset;
    let targetTop = scrollTopDOM;

    if (alignment === "start") targetTop = absoluteTop - state.pTop;
    else if (alignment === "center")
      targetTop =
        absoluteTop -
        state.pTop -
        (parentHeight - state.pTop - bottomPadding - elRect.height) / 2;
    else if (alignment === "end")
      targetTop = scrollTopDOM + offsetBottom + bottomPadding;
    else {
      // nearest
      if (offsetTopActual < state.pTop) targetTop = absoluteTop - state.pTop;
      else if (offsetBottom >= -bottomPadding)
        targetTop = scrollTopDOM + offsetBottom + bottomPadding;
      else targetTop = state.targetY;
    }

    const maxScrollY = isRoot
      ? _docEl.scrollHeight - window.innerHeight
      : parent.scrollHeight - parent.clientHeight;
    state.targetY = Math.max(0, Math.min(targetTop, maxScrollY));
  }

  if (globalAnimId === null) {
    lastTime = performance.now();
    globalAnimId = requestAnimationFrame(globalStep);
  }
}
